import React, { useState, useEffect, useRef } from 'react';
import Navigation from '../components/Navigation';
import QRCodeModal from '../components/QRCodeModal';
import AdvancedQRCodeGenerator from '../components/AdvancedQRCodeGenerator';
import NotificationModal from '../components/NotificationModal';
import ConfirmModal from '../components/ConfirmModal';
import { qrCodeService } from '../services/api';
import { config } from '../config';
import QRCodeStyling from 'qr-code-styling';
import JSZip from 'jszip';
import './QRCodeList.css';

// Ícones SVG
const Icons = {
  Search: () => <svg xmlns="http://www.w3.org/2000/svg" className="search-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
  Download: () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
  Edit: () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
  Trash: () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
  Paint: () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path></svg>,
  Plus: () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
  ExternalLink: () => <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
};

function QRCodeList({ onLogout }) {
  // Estados principais
  const [qrCodes, setQrCodes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAdvancedGenerator, setShowAdvancedGenerator] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);
  const [editingQR, setEditingQR] = useState(null);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [selectedQRs, setSelectedQRs] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  // Busca com debounce
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Loading states separados para melhor UX
  const [initialLoading, setInitialLoading] = useState(true); // Só no primeiro carregamento
  const [isRefreshing, setIsRefreshing] = useState(false); // Loading sutil para paginação/busca

  const qrCodeInstancesRef = useRef({});
  const [openDownloadMenu, setOpenDownloadMenu] = useState(null);

  // Debounce da busca (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // Resetar página ao buscar
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Carregar QR Codes quando mudar página ou termo de busca
  useEffect(() => {
    // Primeiro carregamento
    if (qrCodes.length === 0 && !debouncedSearch) {
      loadQRCodes(true);
    } else {
      loadQRCodes(false);
    }
  }, [currentPage, debouncedSearch]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setOpenDownloadMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadQRCodes = async (isInitial = false) => {
    try {
      // Só mostra loading bloqueante no primeiro carregamento
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setIsRefreshing(true); // Loading sutil
      }

      const response = await qrCodeService.listPaginated({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch
      });

      setQrCodes(response.items || []);
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.totalCount || 0);
    } catch (err) {
      console.error('Erro ao carregar QR Codes:', err);
      setError('Erro ao carregar QR Codes');
    } finally {
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  };
  const handleDownloadClick = (id, e) => {
    e.stopPropagation();
    setOpenDownloadMenu(openDownloadMenu === id ? null : id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Renderizar QR Codes após carregar/paginar
  useEffect(() => {
    if (!initialLoading && qrCodes.length > 0) {
      qrCodes.forEach(qr => {
        const container = document.getElementById(`qr-custom-${qr.id}`);
        if (container) {
          container.innerHTML = '';
          const qrOptions = qr.customizacao ? {
            ...qr.customizacao,
            data: getQRUrl(qr.id),
            width: 150,
            height: 150,
            margin: qr.customizacao.margin !== undefined
              ? Math.round((qr.customizacao.margin / (qr.customizacao.width || 1024)) * 150)
              : 0
          } : {
            width: 150,
            height: 150,
            type: 'canvas',
            data: getQRUrl(qr.id),
            margin: 0,
            qrOptions: { typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'H' },
            imageOptions: { hideBackgroundDots: true, imageSize: 0.4, margin: 0 },
            dotsOptions: { color: '#000000', type: 'rounded' },
            backgroundOptions: { color: '#ffffff' },
            cornersSquareOptions: { color: '#000000', type: 'extra-rounded' },
            cornersDotOptions: { color: '#000000', type: 'dot' }
          };

          const qrCode = new QRCodeStyling(qrOptions);
          qrCode.append(container);
          qrCodeInstancesRef.current[qr.id] = qrCode;
        }
      });
    }
  }, [qrCodes, initialLoading]);

  const handleCreate = () => {
    setEditingQR(null);
    setShowModal(true);
  };

  const handleEdit = (qr) => {
    setEditingQR(qr);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      message: 'Tem certeza que deseja excluir este QR Code?',
      onConfirm: async () => {
        try {
          await qrCodeService.delete(id);
          await loadQRCodes();
          setNotification({ message: 'QR Code excluído com sucesso!', type: 'success' });
        } catch (err) {
          setNotification({ message: 'Erro ao excluir QR Code', type: 'error' });
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleSave = async (message) => {
    setShowModal(false);
    await loadQRCodes();
    if (message) {
      setNotification({ message: message, type: 'success' });
    }
  };

  const getQRUrl = (id) => {
    return `${config.publicUrl}/${id}`;
  };

  const handleCustomize = (qr) => {
    setSelectedQR(qr);
    setShowAdvancedGenerator(true);
  };

  const getCustomQRCodeComponent = (qr) => {
    return (
      <div
        id={`qr-custom-${qr.id}`}
        className="qr-preview-area"
      />
    );
  };

  const handleToggleSelect = (id) => {
    const newSelected = new Set(selectedQRs);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedQRs(newSelected);
  };

  const handleSelectAll = () => {
    // Seleciona/desmarca apenas os QR Codes da página atual
    const currentPageIds = qrCodes.map(qr => qr.id);
    const allSelected = currentPageIds.every(id => selectedQRs.has(id));

    if (allSelected) {
      // Desmarcar todos da página atual
      const newSelected = new Set(selectedQRs);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedQRs(newSelected);
    } else {
      // Selecionar todos da página atual
      const newSelected = new Set(selectedQRs);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedQRs(newSelected);
    }
  };

  const downloadSingleQR = async (qr, format = 'png') => {
    try {
      // Re-create options for high resolution
      const downloadOptions = qr.customizacao ? {
        ...qr.customizacao,
        data: getQRUrl(qr.id)
      } : {
        width: 1024,
        height: 1024,
        type: 'canvas',
        data: getQRUrl(qr.id),
        margin: 0,
        qrOptions: { typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'H' },
        imageOptions: { hideBackgroundDots: true, imageSize: 0.4, margin: 0 },
        dotsOptions: { color: '#000000', type: 'rounded' },
        backgroundOptions: { color: '#ffffff' },
        cornersSquareOptions: { color: '#000000', type: 'extra-rounded' },
        cornersDotOptions: { color: '#000000', type: 'dot' }
      };

      const tempQR = new QRCodeStyling(downloadOptions);
      tempQR.download({ name: qr.nome || qr.id, extension: format });
    } catch (err) {
      setNotification({ message: 'Erro ao fazer download', type: 'error' });
    }
  };

  const downloadMultipleQRs = async () => {
    if (selectedQRs.size === 0) return;

    try {
      setDownloading(true);
      const zip = new JSZip();
      const qrsFolder = zip.folder('qrcodes');

      for (const qrId of selectedQRs) {
        const qr = qrCodes.find(q => q.id === qrId);
        if (!qr) continue;

        const downloadOptions = qr.customizacao ? { ...qr.customizacao, data: getQRUrl(qr.id) } : {
          width: 1024, height: 1024, type: 'canvas', data: getQRUrl(qr.id),
          qrOptions: { errorCorrectionLevel: 'H' },
        };
        const tempQR = new QRCodeStyling(downloadOptions);
        const blob = await tempQR.getRawData('png');
        qrsFolder.file(`${qr.nome || qr.id}.png`, blob);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `qrcodes-${new Date().getTime()}.zip`;
      link.click();

      setNotification({ message: `${selectedQRs.size} QR Code(s) baixados!`, type: 'success' });
      setSelectedQRs(new Set());
    } catch (err) {
      setNotification({ message: 'Erro ao criar ZIP', type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="qrcode-list-page">
        <Navigation onLogout={onLogout} />
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando seus QR Codes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qrcode-list-page">
      <Navigation onLogout={onLogout} />

      <div className="container">
        <div className="page-header">
          <h1>Meus QR Codes</h1>
          <div className="header-actions">
            {selectedQRs.size > 0 && (
              <button
                className="btn btn-success"
                onClick={downloadMultipleQRs}
                disabled={downloading}
              >
                <Icons.Download />
                {downloading ? 'Gerando ZIP...' : `Baixar (${selectedQRs.size})`}
              </button>
            )}
            <button className="btn btn-primary" onClick={handleCreate}>
              <Icons.Plus /> Novo QR Code
            </button>
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="controls-bar">
          <div className="search-input-wrapper">
            <Icons.Search />
            <input
              type="text"
              placeholder="Buscar por nome, ID ou destino..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <span style={{ color: isRefreshing ? 'var(--primary-500)' : 'var(--secondary-500)', fontSize: '0.9rem', fontWeight: isRefreshing ? 500 : 400 }}>
            {isRefreshing ? '⏳ Atualizando...' : `${totalCount} QR Code${totalCount !== 1 ? 's' : ''} encontrado${totalCount !== 1 ? 's' : ''}`}
          </span>

          {qrCodes.length > 0 && (
            <button
              className="btn btn-secondary"
              onClick={handleSelectAll}
            >
              {qrCodes.every(qr => selectedQRs.has(qr.id)) ? 'Desmarcar Página' : 'Selecionar Página'}
            </button>
          )}
        </div>

        {qrCodes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>{searchTerm ? `Nenhum QR Code encontrado para "${searchTerm}"` : 'Você ainda não criou nenhum QR Code.'}</p>
            {!searchTerm && (
              <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={handleCreate}>
                Criar meu primeiro QR Code
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Paginação Superior */}
            {totalPages > 1 && (
              <div className="pagination pagination-top">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            )}

            <div className="qr-grid" style={{ opacity: isRefreshing ? 0.6 : 1, transition: 'opacity 0.2s ease', pointerEvents: isRefreshing ? 'none' : 'auto' }}>
              {qrCodes.map((qr) => (
                <div key={qr.id} className={`qr-card ${selectedQRs.has(qr.id) ? 'selected' : ''}`}>
                  <div className="qr-card-header">
                    <div className="qr-header-info">
                      <div className="qr-title" title={qr.nome}>{qr.nome || 'Sem nome'}</div>
                      <div className="qr-date-badge">
                        📅 Criado em {formatDate(qr.dataCriacao)}
                      </div>
                      <div className="qr-access-badge" title="Total de acessos">
                        👁️ {qr.acessos || 0} acesso{(qr.acessos || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedQRs.has(qr.id)}
                      onChange={() => handleToggleSelect(qr.id)}
                      className="qr-checkbox"
                    />
                  </div>

                  <div className="qr-image">
                    {getCustomQRCodeComponent(qr)}
                  </div>

                  <div className="qr-info">
                    <div className="qr-meta">
                      <span>ID: <strong>{qr.id}</strong></span>
                      {qr.urlDestino && (
                        <span className="qr-destination" title={qr.urlDestino}>
                          <Icons.ExternalLink /> {qr.urlDestino.length > 30 ? qr.urlDestino.substring(0, 30) + '...' : qr.urlDestino}
                        </span>
                      )}

                      <span className="qr-link-copy" onClick={() => {
                        navigator.clipboard.writeText(getQRUrl(qr.id));
                        setNotification({ message: 'Link copiado!', type: 'success' });
                        setTimeout(() => setNotification(null), 2000);
                      }}>
                        <Icons.ExternalLink /> Copiar Link
                      </span>
                    </div>

                    <div className="qr-actions-row">
                      <button className="btn btn-secondary btn-sm" onClick={() => handleCustomize(qr)}>
                        <Icons.Paint /> Estilo
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(qr)}>
                        <Icons.Edit /> Editar
                      </button>

                      <div className="download-wrapper">
                        <button
                          className={`btn btn-secondary btn-icon-only ${openDownloadMenu === qr.id ? 'active' : ''}`}
                          onClick={(e) => handleDownloadClick(qr.id, e)}
                          title="Download"
                        >
                          <Icons.Download />
                        </button>

                        {openDownloadMenu === qr.id && (
                          <div className="download-dropdown">
                            <div className="download-option" onClick={() => downloadSingleQR(qr, 'png')}>
                              PNG
                            </div>
                            <div className="download-option" onClick={() => downloadSingleQR(qr, 'jpeg')}>
                              JPEG
                            </div>
                            <div className="download-option" onClick={() => downloadSingleQR(qr, 'svg')}>
                              SVG
                            </div>
                            <div className="download-option" onClick={() => downloadSingleQR(qr, 'webp')}>
                              WEBP
                            </div>
                          </div>
                        )}
                      </div>

                      <button className="btn btn-secondary btn-icon-only" onClick={() => handleDelete(qr.id)} style={{ color: 'var(--danger)' }}>
                        <Icons.Trash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginação Inferior (com scroll para o topo) */}
            {totalPages > 1 && (
              <div className="pagination pagination-bottom">
                <button
                  className="page-btn"
                  onClick={() => {
                    setCurrentPage(p => Math.max(1, p - 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentPage(page);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className="page-btn"
                  onClick={() => {
                    setCurrentPage(p => Math.min(totalPages, p + 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </>
        )}

        {showModal && (
          <QRCodeModal
            qrCode={editingQR}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
          />
        )}

        {showAdvancedGenerator && selectedQR && (
          <AdvancedQRCodeGenerator
            url={getQRUrl(selectedQR.id)}
            qrCodeName={selectedQR.nome || selectedQR.id}
            qrCodeId={selectedQR.id}
            qrCodeDestino={selectedQR.urlDestino}
            initialCustomization={selectedQR.customizacao}
            onClose={() => {
              setShowAdvancedGenerator(false);
              setSelectedQR(null);
              loadQRCodes();
            }}
          />
        )}

        {notification && (
          <NotificationModal
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {confirmDialog && (
          <ConfirmModal
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={confirmDialog.onCancel}
          />
        )}
      </div>
    </div>
  );
}

export default QRCodeList;
